/**
 * Eye-soothing, rich chromatic color palettes for Humber DCS Sonography courses.
 * Inspired by the vibrant deep medical gradient of the Navbar (sky-700 via cyan-600 to teal-600).
 * Non-black, rich saturated tones with luminous white contrast for effortless readability.
 */

export const SUBJECT_THEMES = {
  DCS5000: {
    // Introduction to Echocardiography - Deep Ocean Sky & Cyan (Matching Navbar tone)
    cardBg: "bg-gradient-to-br from-sky-700 via-cyan-600 to-teal-600 shadow-lg shadow-sky-900/15",
    cardBorder: "border-sky-400/40 hover:border-white/70",
    badgeBg: "bg-white/20 text-white border-white/30 backdrop-blur-xs",
    tableBadge: "bg-sky-100 text-sky-800 border-sky-300",
    iconBg: "bg-white text-sky-700 shadow-md",
    progressBar: "[&>div]:bg-white",
    tableProgressBar: "[&>div]:bg-sky-600",
    bannerGradient: "from-sky-700 via-cyan-600 to-teal-600",
    bannerBorder: "border-sky-400/40",
    accentText: "text-sky-200",
    pillTone: "bg-sky-100 text-sky-800",
  },
  DCS5002: {
    // Cardiac Anatomy & Physiology - Rich Coral Rose & Crimson
    cardBg: "bg-gradient-to-br from-rose-700 via-pink-600 to-rose-600 shadow-lg shadow-rose-900/15",
    cardBorder: "border-rose-400/40 hover:border-white/70",
    badgeBg: "bg-white/20 text-white border-white/30 backdrop-blur-xs",
    tableBadge: "bg-rose-100 text-rose-800 border-rose-300",
    iconBg: "bg-white text-rose-700 shadow-md",
    progressBar: "[&>div]:bg-white",
    tableProgressBar: "[&>div]:bg-rose-600",
    bannerGradient: "from-rose-700 via-pink-600 to-rose-600",
    bannerBorder: "border-rose-400/40",
    accentText: "text-rose-200",
    pillTone: "bg-rose-100 text-rose-800",
  },
  DCS5003: {
    // Cardiac Pathophysiology I - Warm Amber & Cinnamon Orange
    cardBg: "bg-gradient-to-br from-amber-700 via-orange-600 to-amber-600 shadow-lg shadow-amber-900/15",
    cardBorder: "border-amber-400/40 hover:border-white/70",
    badgeBg: "bg-white/20 text-white border-white/30 backdrop-blur-xs",
    tableBadge: "bg-amber-100 text-amber-800 border-amber-300",
    iconBg: "bg-white text-amber-700 shadow-md",
    progressBar: "[&>div]:bg-white",
    tableProgressBar: "[&>div]:bg-amber-600",
    bannerGradient: "from-amber-700 via-orange-600 to-amber-600",
    bannerBorder: "border-amber-400/40",
    accentText: "text-amber-200",
    pillTone: "bg-amber-100 text-amber-800",
  },
  DCS5004: {
    // Ultrasound Physics & Instrumentation - Royal Cobalt & Indigo Blue
    cardBg: "bg-gradient-to-br from-indigo-700 via-indigo-600 to-blue-600 shadow-lg shadow-indigo-900/15",
    cardBorder: "border-indigo-400/40 hover:border-white/70",
    badgeBg: "bg-white/20 text-white border-white/30 backdrop-blur-xs",
    tableBadge: "bg-indigo-100 text-indigo-800 border-indigo-300",
    iconBg: "bg-white text-indigo-700 shadow-md",
    progressBar: "[&>div]:bg-white",
    tableProgressBar: "[&>div]:bg-indigo-600",
    bannerGradient: "from-indigo-700 via-indigo-600 to-blue-600",
    bannerBorder: "border-indigo-400/40",
    accentText: "text-indigo-200",
    pillTone: "bg-indigo-100 text-indigo-800",
  },
  DCS5005: {
    // Sonography Principles & Scanning - Fresh Jade Emerald & Mint
    cardBg: "bg-gradient-to-br from-emerald-700 via-teal-600 to-emerald-600 shadow-lg shadow-emerald-900/15",
    cardBorder: "border-emerald-400/40 hover:border-white/70",
    badgeBg: "bg-white/20 text-white border-white/30 backdrop-blur-xs",
    tableBadge: "bg-emerald-100 text-emerald-800 border-emerald-300",
    iconBg: "bg-white text-emerald-700 shadow-md",
    progressBar: "[&>div]:bg-white",
    tableProgressBar: "[&>div]:bg-emerald-600",
    bannerGradient: "from-emerald-700 via-teal-600 to-emerald-600",
    bannerBorder: "border-emerald-400/40",
    accentText: "text-emerald-200",
    pillTone: "bg-emerald-100 text-emerald-800",
  },
  DCS5006: {
    // Patient Care & Professional Practice - Deep Marine Teal & Seafoam
    cardBg: "bg-gradient-to-br from-teal-700 via-cyan-700 to-teal-600 shadow-lg shadow-teal-900/15",
    cardBorder: "border-teal-400/40 hover:border-white/70",
    badgeBg: "bg-white/20 text-white border-white/30 backdrop-blur-xs",
    tableBadge: "bg-teal-100 text-teal-800 border-teal-300",
    iconBg: "bg-white text-teal-700 shadow-md",
    progressBar: "[&>div]:bg-white",
    tableProgressBar: "[&>div]:bg-teal-600",
    bannerGradient: "from-teal-700 via-cyan-700 to-teal-600",
    bannerBorder: "border-teal-400/40",
    accentText: "text-teal-200",
    pillTone: "bg-teal-100 text-teal-800",
  },
  IPE101: {
    // Interprofessional Education - Royal Amethyst & Violet
    cardBg: "bg-gradient-to-br from-violet-700 via-purple-600 to-violet-600 shadow-lg shadow-violet-900/15",
    cardBorder: "border-violet-400/40 hover:border-white/70",
    badgeBg: "bg-white/20 text-white border-white/30 backdrop-blur-xs",
    tableBadge: "bg-violet-100 text-violet-800 border-violet-300",
    iconBg: "bg-white text-violet-700 shadow-md",
    progressBar: "[&>div]:bg-white",
    tableProgressBar: "[&>div]:bg-violet-600",
    bannerGradient: "from-violet-700 via-purple-600 to-violet-600",
    bannerBorder: "border-violet-400/40",
    accentText: "text-violet-200",
    pillTone: "bg-violet-100 text-violet-800",
  },
  DCS5500: {
    // Advanced Echocardiography - Bright Arctic Cyan & Electric Blue
    cardBg: "bg-gradient-to-br from-cyan-700 via-blue-600 to-cyan-600 shadow-lg shadow-cyan-900/15",
    cardBorder: "border-cyan-400/40 hover:border-white/70",
    badgeBg: "bg-white/20 text-white border-white/30 backdrop-blur-xs",
    tableBadge: "bg-cyan-100 text-cyan-800 border-cyan-300",
    iconBg: "bg-white text-cyan-700 shadow-md",
    progressBar: "[&>div]:bg-white",
    tableProgressBar: "[&>div]:bg-cyan-600",
    bannerGradient: "from-cyan-700 via-blue-600 to-cyan-600",
    bannerBorder: "border-cyan-400/40",
    accentText: "text-cyan-200",
    pillTone: "bg-cyan-100 text-cyan-800",
  },
  DCS5501: {
    // Valvular Heart Disease - Rich Sunset Orange & Peach
    cardBg: "bg-gradient-to-br from-orange-700 via-amber-600 to-orange-600 shadow-lg shadow-orange-900/15",
    cardBorder: "border-orange-400/40 hover:border-white/70",
    badgeBg: "bg-white/20 text-white border-white/30 backdrop-blur-xs",
    tableBadge: "bg-orange-100 text-orange-800 border-orange-300",
    iconBg: "bg-white text-orange-700 shadow-md",
    progressBar: "[&>div]:bg-white",
    tableProgressBar: "[&>div]:bg-orange-600",
    bannerGradient: "from-orange-700 via-amber-600 to-orange-600",
    bannerBorder: "border-orange-400/40",
    accentText: "text-orange-200",
    pillTone: "bg-orange-100 text-orange-800",
  },
  DCS5502: {
    // Congenital Heart Disease - Vivid Orchid Fuchsia & Magenta
    cardBg: "bg-gradient-to-br from-fuchsia-700 via-pink-600 to-purple-600 shadow-lg shadow-fuchsia-900/15",
    cardBorder: "border-fuchsia-400/40 hover:border-white/70",
    badgeBg: "bg-white/20 text-white border-white/30 backdrop-blur-xs",
    tableBadge: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300",
    iconBg: "bg-white text-fuchsia-700 shadow-md",
    progressBar: "[&>div]:bg-white",
    tableProgressBar: "[&>div]:bg-fuchsia-600",
    bannerGradient: "from-fuchsia-700 via-pink-600 to-purple-600",
    bannerBorder: "border-fuchsia-400/40",
    accentText: "text-fuchsia-200",
    pillTone: "bg-fuchsia-100 text-fuchsia-800",
  },
  DCS5503: {
    // Cardiac Pathophysiology II - Deep Ruby & Crimson Red
    cardBg: "bg-gradient-to-br from-red-700 via-rose-600 to-red-600 shadow-lg shadow-red-900/15",
    cardBorder: "border-red-400/40 hover:border-white/70",
    badgeBg: "bg-white/20 text-white border-white/30 backdrop-blur-xs",
    tableBadge: "bg-red-100 text-red-800 border-red-300",
    iconBg: "bg-white text-red-700 shadow-md",
    progressBar: "[&>div]:bg-white",
    tableProgressBar: "[&>div]:bg-red-600",
    bannerGradient: "from-red-700 via-rose-600 to-red-600",
    bannerBorder: "border-red-400/40",
    accentText: "text-red-200",
    pillTone: "bg-red-100 text-red-800",
  },
  DCS5504: {
    // Advanced Physics & Hemodynamics - Deep Royal Amethyst & Purple
    cardBg: "bg-gradient-to-br from-purple-700 via-indigo-600 to-purple-600 shadow-lg shadow-purple-900/15",
    cardBorder: "border-purple-400/40 hover:border-white/70",
    badgeBg: "bg-white/20 text-white border-white/30 backdrop-blur-xs",
    tableBadge: "bg-purple-100 text-purple-800 border-purple-300",
    iconBg: "bg-white text-purple-700 shadow-md",
    progressBar: "[&>div]:bg-white",
    tableProgressBar: "[&>div]:bg-purple-600",
    bannerGradient: "from-purple-700 via-indigo-600 to-purple-600",
    bannerBorder: "border-purple-400/40",
    accentText: "text-purple-200",
    pillTone: "bg-purple-100 text-purple-800",
  },
  DCS5505: {
    // Clinical Applications & Case Studies - Vibrant Forest Emerald & Sage
    cardBg: "bg-gradient-to-br from-emerald-700 via-teal-600 to-green-600 shadow-lg shadow-emerald-900/15",
    cardBorder: "border-emerald-400/40 hover:border-white/70",
    badgeBg: "bg-white/20 text-white border-white/30 backdrop-blur-xs",
    tableBadge: "bg-emerald-100 text-emerald-800 border-emerald-300",
    iconBg: "bg-white text-emerald-700 shadow-md",
    progressBar: "[&>div]:bg-white",
    tableProgressBar: "[&>div]:bg-emerald-600",
    bannerGradient: "from-emerald-700 via-teal-600 to-green-600",
    bannerBorder: "border-emerald-400/40",
    accentText: "text-emerald-200",
    pillTone: "bg-emerald-100 text-emerald-800",
  },
  DCS5506: {
    // Professional Ethics & Law - Rich Royal Blue & Cerulean
    cardBg: "bg-gradient-to-br from-blue-700 via-indigo-600 to-sky-600 shadow-lg shadow-blue-900/15",
    cardBorder: "border-blue-400/40 hover:border-white/70",
    badgeBg: "bg-white/20 text-white border-white/30 backdrop-blur-xs",
    tableBadge: "bg-blue-100 text-blue-800 border-blue-300",
    iconBg: "bg-white text-blue-700 shadow-md",
    progressBar: "[&>div]:bg-white",
    tableProgressBar: "[&>div]:bg-blue-600",
    bannerGradient: "from-blue-700 via-indigo-600 to-sky-600",
    bannerBorder: "border-blue-400/40",
    accentText: "text-blue-200",
    pillTone: "bg-blue-100 text-blue-800",
  },
};

const DEFAULT_THEME = {
  cardBg: "bg-gradient-to-br from-sky-700 via-cyan-600 to-teal-600 shadow-lg shadow-sky-900/15",
  cardBorder: "border-sky-400/40 hover:border-white/70",
  badgeBg: "bg-white/20 text-white border-white/30 backdrop-blur-xs",
  tableBadge: "bg-sky-100 text-sky-800 border-sky-300",
  iconBg: "bg-white text-sky-700 shadow-md",
  progressBar: "[&>div]:bg-white",
  tableProgressBar: "[&>div]:bg-sky-600",
  bannerGradient: "from-sky-700 via-cyan-600 to-teal-600",
  bannerBorder: "border-sky-400/40",
  accentText: "text-sky-200",
  pillTone: "bg-sky-100 text-sky-800",
};

export const getSubjectTheme = (id) => SUBJECT_THEMES[id] || DEFAULT_THEME;
